import { escapeHtml } from '../format';

/**
 * Library-free chart markup (CSS bars and a small SVG donut). Every chart is
 * paired with visible text or a screen-reader list; the graphics themselves
 * are aria-hidden. Inputs that come from the chat are always escaped here.
 */

export interface BarRow {
  readonly label: string;
  readonly value: number;
  /** Visible value text, e.g. "73%". */
  readonly valueText: string;
  /** A palette color (never user input). */
  readonly color: string;
  /** Secondary line under the bar. */
  readonly note?: string;
  /** Short badge after the name, e.g. "나". */
  readonly badge?: string;
}

export type BarScale = 'linear' | 'log';

/** 0..1 bar length. Non-zero values stay visible (at least 3%). */
export function barRatio(value: number, max: number, scale: BarScale = 'linear'): number {
  if (!(value > 0) || !(max > 0)) return 0;
  const raw = scale === 'log' ? Math.log1p(value) / Math.log1p(max) : value / max;
  return Math.min(1, Math.max(0.03, raw));
}

function percentStyle(ratio: number): string {
  return `${Math.round(ratio * 1000) / 10}%`;
}

export function barRowsMarkup(rows: readonly BarRow[], options: { readonly max?: number; readonly scale?: BarScale } = {}): string {
  const max = options.max ?? rows.reduce((best, row) => Math.max(best, row.value), 0);
  return `<ul class="krc-bars">${rows.map((row) => {
    const ratio = barRatio(row.value, max, options.scale);
    const badge = row.badge ? ` <span class="krc-me-badge">${escapeHtml(row.badge)}</span>` : '';
    const note = row.note ? `<p class="krc-bar-note">${escapeHtml(row.note)}</p>` : '';
    return `<li class="krc-bar-row">
        <div class="krc-bar-head"><span class="krc-bar-label"><span class="krc-dot" style="background:${row.color}" aria-hidden="true"></span>${escapeHtml(row.label)}${badge}</span><span class="krc-bar-value">${escapeHtml(row.valueText)}</span></div>
        <div class="krc-bar-track" aria-hidden="true"><span class="krc-bar-fill" style="width:${percentStyle(ratio)};background:${row.color}"></span></div>${note}
      </li>`;
  }).join('')}</ul>`;
}

export interface ColumnChartOptions {
  /** One label per value, used in the screen-reader list and tooltips. */
  readonly labels: readonly string[];
  /** Axis labels under some columns (null = no label). */
  readonly ticks: readonly (string | null)[];
  /** Index of the column to emphasise. */
  readonly highlight: number | null;
  /** Visible one-line summary that doubles as the accessible description. */
  readonly summary: string;
  /** Unit after each value in the screen-reader list, e.g. "개". */
  readonly unit: string;
  readonly className?: string;
}

export function columnChartMarkup(values: readonly number[], options: ColumnChartOptions): string {
  const max = values.reduce((best, value) => Math.max(best, value), 0);
  const columns = values.map((value, index) => {
    const ratio = barRatio(value, max);
    const active = index === options.highlight ? ' is-peak' : '';
    return `<span class="krc-column${active}" title="${escapeHtml(`${options.labels[index] ?? ''} · ${value}${options.unit}`)}"><span style="height:${percentStyle(ratio)}"></span></span>`;
  }).join('');
  const ticks = options.ticks.map((tick) => `<span>${tick ? escapeHtml(tick) : ''}</span>`).join('');
  const list = values.map((value, index) => `<li>${escapeHtml(options.labels[index] ?? '')} ${value}${escapeHtml(options.unit)}</li>`).join('');
  return `<div class="krc-columns-chart ${options.className ?? ''}">
      <div class="krc-columns" aria-hidden="true" style="grid-template-columns:repeat(${values.length}, minmax(0, 1fr))">${columns}</div>
      <div class="krc-column-ticks" aria-hidden="true" style="grid-template-columns:repeat(${values.length}, minmax(0, 1fr))">${ticks}</div>
    </div>
    <p class="krc-chart-summary">${escapeHtml(options.summary)}</p>
    <ul class="krc-sr-only">${list}</ul>`;
}

export interface DonutSegment {
  readonly value: number;
  readonly color: string;
}

/** Decorative donut; the legend next to it carries the numbers. */
export function donutMarkup(segments: readonly DonutSegment[], center: { readonly value: string; readonly label: string }): string {
  const total = segments.reduce((sum, segment) => sum + Math.max(0, segment.value), 0);
  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  const arcs = total > 0 ? segments.filter((segment) => segment.value > 0).map((segment) => {
    const length = (segment.value / total) * circumference;
    const gap = segments.length > 1 ? Math.min(2, length / 4) : 0;
    const arc = `<circle cx="60" cy="60" r="${radius}" fill="none" stroke="${segment.color}" stroke-width="16" stroke-dasharray="${(length - gap).toFixed(2)} ${(circumference - length + gap).toFixed(2)}" stroke-dashoffset="${(-offset).toFixed(2)}" transform="rotate(-90 60 60)"/>`;
    offset += length;
    return arc;
  }).join('') : '';
  return `<div class="krc-donut" aria-hidden="true">
      <svg viewBox="0 0 120 120" focusable="false"><circle cx="60" cy="60" r="${radius}" fill="none" stroke="#efeaf9" stroke-width="16"/>${arcs}</svg>
      <span class="krc-donut-center"><strong>${escapeHtml(center.value)}</strong><small>${escapeHtml(center.label)}</small></span>
    </div>`;
}
